import { useState } from "react";
import { Dialog } from "@mui/material";
import I18n from "common/i18n";
import { btnDelay, usePlaySound } from "common/utils";

type Props = {
  onClose: () => void;
};

// チュートリアル説明カード（白角丸ボーダー + 半透明黒背景 + 白テキスト）
const TipCard = ({ text }: { text: string }) => (
  <div className="bg-textcolor-black/60 border-2 border-white rounded-2xl px-6 py-3">
    <p className="text-body type-body text-white whitespace-nowrap">{text}</p>
  </div>
);

// 指タップアイコン（白枠の丸 + touch_app）
const TapIcon = () => (
  <div className="w-12 h-12 rounded-full border-2 border-white flex-center bg-textcolor-black/40">
    <span className="material-symbols-outlined type-md text-white">touch_app</span>
  </div>
);

// 点線（カードと指アイコンを繋ぐ）
const DottedLine = ({ height = "h-6" }: { height?: string }) => (
  <div className={`${height} w-px border-l-2 border-dashed border-white`} />
);

// タイトル（左右に短い線）
const SectionTitle = ({ text }: { text: string }) => (
  <div className="flex-row-el flex-center gap-3">
    <div className="w-12 h-px bg-white" />
    <p className="text-body type-title text-white">{text}</p>
    <div className="w-12 h-px bg-white" />
  </div>
);

const FooterHint = ({ text }: { text: string }) => (
  <div className="flex-row-el flex-center gap-3">
    <div className="w-8 h-px bg-white" />
    <p className="text-body type-body text-white">{text}</p>
    <div className="w-8 h-px bg-white" />
  </div>
);

export const Tutorial = ({ onClose }: Props) => {
  const [disabled, setDisabled] = useState(false);
  const play = usePlaySound();

  const handleClose = () => {
    if (disabled) return;
    setDisabled(true);
    play();
    btnDelay(onClose);
  };

  return (
    <Dialog
      open
      fullScreen
      componentsProps={{
        backdrop: { style: { backgroundColor: "transparent" } },
      }}
      sx={{ zIndex: 10001, backgroundColor: "transparent" }}
      PaperProps={{ style: { backgroundColor: "transparent" } }}
    >
      <button
        type="button"
        onClick={handleClose}
        className="!absolute w-full h-full bg-textcolor-black/40 border-0 outline-none p-0 cursor-pointer animate-fade-in"
      >
        {/* 上部: チュートリアル タイトル */}
        <div className="absolute top-8 left-1/2 -translate-x-1/2">
          <SectionTitle text={I18n.t("MSG_SOKOBAN_TUTORIAL_TITLE")} />
        </div>

        {/* 中央: ゴール（ボタン）の説明 */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex-col-center gap-3">
          <div className="w-24 h-24 rounded-full border-2 border-white flex-center bg-yellow-200/20">
            <span
              className="material-symbols-outlined type-display"
              style={{ color: "#fdd73e", fontVariationSettings: "'FILL' 1" }}
            >
              radio_button_checked
            </span>
          </div>
          <TapIcon />
          <TipCard text={I18n.t("MSG_SOKOBAN_TUTORIAL_GOAL_DESC")} />
          <p className="text-body type-body text-white">
            {I18n.t("MSG_SOKOBAN_TUTORIAL_GOAL_HINT")}
          </p>
        </div>

        {/* 左下: ジョイスティック説明（SceneJoystick の上） */}
        <div className="absolute bottom-[210px] left-12 flex-col-center gap-2">
          <TipCard text={I18n.t("MSG_SOKOBAN_TUTORIAL_CAMERA_DESC")} />
          <DottedLine />
          <TapIcon />
        </div>

        {/* 右下: コントローラー説明（SokobanController の上） */}
        <div className="absolute bottom-[230px] right-12 flex-col-center gap-2">
          <TipCard text={I18n.t("MSG_SOKOBAN_TUTORIAL_CONTROLLER_DESC")} />
          <DottedLine />
          <div className="flex-row-el flex-center gap-2">
            <div className="w-12 h-12 rounded-full border-2 border-white flex-center bg-textcolor-black/40">
              <span className="material-symbols-outlined type-md text-white">arrow_upward</span>
            </div>
            <div className="w-12 h-12 rounded-full border-2 border-white flex-center bg-textcolor-black/40">
              <span className="material-symbols-outlined type-md text-white">arrow_back</span>
            </div>
            <div className="w-12 h-12 rounded-full border-2 border-white flex-center bg-textcolor-black/40">
              <span className="material-symbols-outlined type-md text-white">arrow_forward</span>
            </div>
            <div className="w-12 h-12 rounded-full border-2 border-white flex-center bg-textcolor-black/40">
              <span className="material-symbols-outlined type-md text-white">
                keyboard_double_arrow_up
              </span>
            </div>
          </div>
        </div>

        {/* 下部: タップしてつづける */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
          <FooterHint text={I18n.t("MSG_SOKOBAN_TUTORIAL_TAP_TO_CONTINUE")} />
        </div>
      </button>
    </Dialog>
  );
};
